import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { LiveComingSoonModal } from "@/components/common/LiveComingSoonModal";
import { useSubscription } from "@/hooks/useSubscription";
import { isLiveEnabledModule } from "@/config/liveModules";

/**
 * Universal Mini-Mirror "LIVE" badge.
 *
 * Status is resolved from the rollout gate (config/liveModules) + the user's
 * tier (useSubscription().isPremium):
 *   - module NOT in LIVE_ENABLED_MODULES        → "comingSoon" (waitlist modal)
 *   - module enabled + Premium                  → "available"  (live entry)
 *   - module enabled + not Premium              → "locked"     (Premium upsell)
 *
 * At launch no mini-mirror module is in LIVE_ENABLED_MODULES (only the Espelho
 * "mirror", which uses its own hero/header entry — not this badge), so every
 * mini-mirror badge resolves to "comingSoon". The available/locked branches are
 * live-ready for when a mini module is enabled later.
 */

export type ModuleKey = "fitness" | "brain" | "language" | "calendar" | "nutrition";

type LiveStatus = "available" | "comingSoon" | "locked";

function resolveLiveStatus(module: ModuleKey, isPremium: boolean): LiveStatus {
  if (!isLiveEnabledModule(module)) return "comingSoon";
  return isPremium ? "available" : "locked";
}

interface MiniMirrorLiveButtonProps {
  module: ModuleKey;
}

export function MiniMirrorLiveButton({ module }: MiniMirrorLiveButtonProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { isPremium } = useSubscription();
  const [open, setOpen] = useState(false);
  const status = resolveLiveStatus(module, isPremium);

  // "available" — a live-enabled module for a Premium user. No mini-mirror is
  // enabled at launch; when one is, its live entry renders here.
  if (status === "available") return null;

  const handleTap = () => {
    if (status === "locked") {
      // Premium upsell — same path the rest of the app uses for gated features.
      navigate("/pricing");
      return;
    }
    setOpen(true); // comingSoon → waitlist modal
  };

  return (
    <>
      <motion.button
        type="button"
        onClick={handleTap}
        animate={{ opacity: [0.78, 1, 0.78] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-2 right-2 z-30 flex items-center gap-1 px-1.5 py-0.5 rounded-md font-display text-[9px] font-semibold tracking-[0.18em] uppercase active:scale-95 transition-transform"
        style={{
          background: "hsl(187 72% 50% / 0.10)",
          border: "1px solid hsl(187 72% 50% / 0.55)",
          color: "hsl(187 72% 65%)",
          boxShadow: "0 0 10px hsl(187 72% 50% / 0.25)",
        }}
        aria-label={t("common.live")}
      >
        <Clock size={9} style={{ color: "hsl(187 72% 65%)" }} />
        <span>{t("common.live")}</span>
      </motion.button>

      {status === "comingSoon" && (
        <LiveComingSoonModal open={open} onOpenChange={setOpen} module={module} />
      )}
    </>
  );
}
